import React, { useState, useEffect } from 'react';
import { createRecipe, generateRecipeWithAI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import '../styles/forms.css';
import '../styles/ai.css';
import { useAuth } from '../context/authContext';

const CreateRecipe = () => {
    const [formData, setFormData] = useState({
        name: '',
        category: '',
        cooking_time: '',
        ingredients: [''],
        prerequisites: [''],
        steps: [''],
        images: [],
    });
    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [error, setError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    
    // AI Context Generation State
    const [urlContext, setUrlContext] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [aiGenerated, setAiGenerated] = useState(false);
    const [highlightFields, setHighlightFields] = useState({});
    
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();

    const categories = [
        'Breakfast', 'Brunch', 'Lunch', 'Dinner', 'Snack', 
        'Dessert', 'Supper', 'Appetizer', 'Side', 'Drink', 
        'Bread', 'Salad', 'Soup'
    ];

    // Auto-resize textareas on mount and data changes
    useEffect(() => {
        const resizeTextareas = () => {
            document.querySelectorAll('textarea').forEach(textarea => {
                textarea.style.height = 'auto';
                textarea.style.height = textarea.scrollHeight + 'px';
            });
        };
        
        setTimeout(resizeTextareas, 0);
    }, [formData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleListChange = (listName, index, value) => {
        setFormData(prev => ({
            ...prev,
            [listName]: prev[listName].map((item, i) => i === index ? value : item)
        }));
    };

    const addListItem = (listName) => {
        setFormData(prev => ({
            ...prev,
            [listName]: [...prev[listName], '']
        }));
    };

    const removeListItem = (listName, index) => {
        if (formData[listName].length > 1) {
            setFormData(prev => ({
                ...prev,
                [listName]: prev[listName].filter((_, i) => i !== index)
            }));
        }
    };

    const handleImageUpload = (files) => {
        const fileArray = Array.from(files);
        setImageFiles(prev => [...prev, ...fileArray]);
        
        // Create previews
        fileArray.forEach(file => {
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreviews(prev => [...prev, e.target.result]);
            };
            reader.readAsDataURL(file);
        });
    };

    const handleFileInput = (e) => {
        handleImageUpload(e.target.files);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleImageUpload(e.dataTransfer.files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const removeImage = (index) => {
        setImageFiles(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const getCuisineBadgeClass = (category) => {
        if (!category) return 'badge-default';
        return `badge-${category.toLowerCase()}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const { name, category, cooking_time, ingredients, prerequisites, steps } = formData;

        // Validation
        if (!name.trim()) {
            setError('Recipe name is required.');
            return;
        }
        if (!category) {
            setError('Please select a category.');
            return;
        }
        if (!cooking_time || cooking_time <= 0) {
            setError('Please enter a valid cooking time.');
            return;
        }
        
        const validIngredients = ingredients.filter(ing => ing.trim());
        if (validIngredients.length === 0) {
            setError('Please add at least one ingredient.');
            return;
        }
        
        const validPrerequisites = prerequisites.filter(pre => pre.trim());
        if (validPrerequisites.length === 0) {
            setError('Please add at least one prerequisite.');
            return;
        }
        
        const validSteps = steps.filter(step => step.trim());
        if (validSteps.length === 0) {
            setError('Please add at least one cooking step.');
            return;
        }

        try {
            if (!currentUser) throw new Error('User not authenticated');
            const token = await currentUser.getIdToken();

            // Combine prerequisites and steps into instructions object
            const instructions = {
                prerequisites: validPrerequisites.map(p => p.trim()).filter(Boolean),
                steps: validSteps.map(s => s.trim()).filter(Boolean)
            };

            // Validate instructions format
            if (!instructions.prerequisites.length || !instructions.steps.length) {
                throw new Error('Both prerequisites and steps are required');
            }

            const submitData = new FormData();
            submitData.append('name', name);
            submitData.append('category', category);
            submitData.append('cooking_time', cooking_time);
            submitData.append('instructions', JSON.stringify(instructions));
            submitData.append('ingredients', JSON.stringify(validIngredients));

            imageFiles.forEach((image) => {
                submitData.append('images', image);
            });

            await createRecipe(submitData, token);
            navigate('/my-recipes');
        } catch (err) {
            console.error('Error creating recipe:', err);
            setError('Failed to create recipe. Please try again.');
        }
    };

    const handleAIGeneration = async () => {
        if (!urlContext.trim()) {
            setError('Please enter a valid recipe URL for AI context.');
            return;
        }

        if (!currentUser) {
            setError('You must be logged in to use AI generation.');
            return;
        }

        try {
            setIsGenerating(true);
            setError('');

            const token = await currentUser.getIdToken();
            
            // Prepare user recipe data
            const userRecipeData = {
                name: formData.name || '',
                category: formData.category || '',
                cooking_time: parseInt(formData.cooking_time) || 0,
                ingredients: formData.ingredients.filter(item => item.trim() !== ''),
                prerequisites: formData.prerequisites.filter(item => item.trim() !== ''),
                steps: formData.steps.filter(item => item.trim() !== '')
            };

            const enhancedRecipe = await generateRecipeWithAI({
                userRecipeData,
                url: urlContext
            }, token);

            // Update form with enhanced data
            const newFormData = {
                ...formData,
                name: enhancedRecipe.name || formData.name,
                category: enhancedRecipe.category || formData.category,
                cooking_time: enhancedRecipe.cooking_time || formData.cooking_time,
                ingredients: enhancedRecipe.ingredients && enhancedRecipe.ingredients.length > 0 
                    ? enhancedRecipe.ingredients 
                    : (formData.ingredients[0] ? formData.ingredients : ['']),
                prerequisites: enhancedRecipe.instructions?.prerequisites && enhancedRecipe.instructions.prerequisites.length > 0
                    ? enhancedRecipe.instructions.prerequisites
                    : (formData.prerequisites[0] ? formData.prerequisites : ['']),
                steps: enhancedRecipe.instructions?.steps && enhancedRecipe.instructions.steps.length > 0
                    ? enhancedRecipe.instructions.steps
                    : (formData.steps[0] ? formData.steps : [''])
            };

            // Track which fields were changed
            const fieldsChanged = {
                name: enhancedRecipe.name && enhancedRecipe.name !== formData.name,
                category: enhancedRecipe.category && enhancedRecipe.category !== formData.category,
                cooking_time: enhancedRecipe.cooking_time && enhancedRecipe.cooking_time !== formData.cooking_time,
                ingredients: enhancedRecipe.ingredients && JSON.stringify(enhancedRecipe.ingredients) !== JSON.stringify(formData.ingredients),
                prerequisites: enhancedRecipe.instructions?.prerequisites && JSON.stringify(enhancedRecipe.instructions.prerequisites) !== JSON.stringify(formData.prerequisites),
                steps: enhancedRecipe.instructions?.steps && JSON.stringify(enhancedRecipe.instructions.steps) !== JSON.stringify(formData.steps)
            };

            setFormData(newFormData);
            setHighlightFields(fieldsChanged);
            setAiGenerated(true);
            setError('');

            // Force re-render of textareas with new content
            setTimeout(() => {
                document.querySelectorAll('textarea').forEach(textarea => {
                    textarea.style.height = 'auto';
                    textarea.style.height = textarea.scrollHeight + 'px';
                });
            }, 100);
        } catch (err) {
            console.error('❌ Error generating recipe with AI:', err);
            setError(err.message || 'Failed to generate recipe with AI. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="create-recipe-container">
            <h1 className="create-recipe-title">Create New Recipe</h1>
            
            <form onSubmit={handleSubmit} className="create-recipe-form">
                {/* Basic Information Section */}
                <div className="form-section">
                    <h3 className="section-title">Basic Information</h3>
                    
                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">Recipe Name</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className={`form-input ${highlightFields.name ? 'ai-enhanced-field' : ''}`}
                                placeholder="Enter recipe name..."
                                required
                            />
                        </div>
                        
                        <div className="form-group">
                            <label className="form-label">Cooking Time (minutes)</label>
                            <input
                                type="number"
                                name="cooking_time"
                                value={formData.cooking_time}
                                onChange={handleChange}
                                className={`form-input ${highlightFields.cooking_time ? 'ai-enhanced-field' : ''}`}
                                placeholder="30"
                                min="1"
                                required
                            />
                        </div>
                    </div>
                    
                    <div className="form-group">
                        <label className="form-label">Category</label>
                        <select
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            className={`form-select ${highlightFields.category ? 'ai-enhanced-field' : ''}`}
                            required
                        >
                            <option value="">Select a category...</option>
                            {categories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                        {formData.category && (
                            <div className={`recipe-card-category-badge ${getCuisineBadgeClass(formData.category)} mt-1`} style={{position: 'relative', display: 'inline-block', top: 'auto', right: 'auto'}}>
                                {formData.category}
                            </div>
                        )}
                    </div>
                </div>

                {/* Images Section */}
                <div className="form-section">
                    <h3 className="section-title">Recipe Images</h3>
                    
                    <div 
                        className={`image-upload-section ${dragOver ? 'drag-over' : ''}`}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onClick={() => document.getElementById('image-input').click()}
                    >
                        <div className="upload-icon">📷</div>
                        <div className="upload-text">
                            <strong>Click to upload</strong> or drag and drop images here
                        </div>
                        <div style={{color: 'var(--color-medium-gray)', fontSize: '0.85rem'}}>
                            PNG, JPG, GIF up to 10MB each
                        </div>
                        <input
                            id="image-input"
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleFileInput}
                            style={{display: 'none'}}
                        />
                    </div>
                    
                    {imagePreviews.length > 0 && (
                        <div className="image-previews">
                            {imagePreviews.map((preview, index) => (
                                <div key={index} className="image-preview-container">
                                    <img src={preview} alt={`Preview ${index + 1}`} className="image-preview" />
                                    <button
                                        type="button"
                                        className="image-remove-btn"
                                        onClick={() => removeImage(index)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Ingredients Section */}
                <div className={`form-section ${highlightFields.ingredients ? 'ai-enhanced-section' : ''}`}>
                    <h3 className="section-title">Ingredients</h3>
                    
                    <div className="dynamic-list">
                        {formData.ingredients.map((ingredient, index) => (
                            <div key={index} className="list-item">
                                <span style={{minWidth: '2rem', color: 'var(--color-medium-gray)'}}>{index + 1}.</span>
                                <textarea
                                    value={ingredient}
                                    onChange={(e) => handleListChange('ingredients', index, e.target.value)}
                                    placeholder="Enter ingredient..."
                                    rows={1}
                                    style={{ 
                                        height: 'auto', 
                                        overflow: 'hidden', 
                                        resize: 'none' 
                                    }}
                                    onInput={(e) => {
                                        e.target.style.height = 'auto';
                                        e.target.style.height = e.target.scrollHeight + 'px';
                                    }}
                                />
                                {formData.ingredients.length > 1 && (
                                    <button
                                        type="button"
                                        className="remove-item-btn"
                                        onClick={() => removeListItem('ingredients', index)}
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        ))}
                        <button
                            type="button"
                            className="add-item-btn small-text"
                            onClick={() => addListItem('ingredients')}
                        >
                            + Add Ingredient
                        </button>
                    </div>
                </div>

                {/* Prerequisites Section */}
                <div className={`form-section ${highlightFields.prerequisites ? 'ai-enhanced-section' : ''}`}>
                    <h3 className="section-title">Prerequisites</h3>
                    
                    <div className="dynamic-list">
                        {formData.prerequisites.map((prerequisite, index) => (
                            <div key={index} className="list-item">
                                <span style={{minWidth: '2rem', color: 'var(--color-medium-gray)'}}>{index + 1}.</span>
                                <textarea
                                    value={prerequisite}
                                    onChange={(e) => handleListChange('prerequisites', index, e.target.value)}
                                    placeholder="e.g., Preheat oven to 350°F..."
                                    rows={1}
                                    style={{ 
                                        height: 'auto', 
                                        overflow: 'hidden', 
                                        resize: 'none' 
                                    }}
                                    onInput={(e) => {
                                        e.target.style.height = 'auto';
                                        e.target.style.height = e.target.scrollHeight + 'px';
                                    }}
                                />
                                {formData.prerequisites.length > 1 && (
                                    <button
                                        type="button"
                                        className="remove-item-btn"
                                        onClick={() => removeListItem('prerequisites', index)}
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        ))}
                        <button
                            type="button"
                            className="add-item-btn small-text"
                            onClick={() => addListItem('prerequisites')}
                        >
                            + Add Prerequisite
                        </button>
                    </div>
                </div>

                {/* Cooking Steps Section */}
                <div className={`form-section ${highlightFields.steps ? 'ai-enhanced-section' : ''}`}>
                    <h3 className="section-title">Cooking Steps</h3>
                    
                    <div className="dynamic-list">
                        {formData.steps.map((step, index) => (
                            <div key={index} className="list-item">
                                <span style={{minWidth: '2rem', color: 'var(--color-medium-gray)'}}>{index + 1}.</span>
                                <textarea
                                    value={step}
                                    onChange={(e) => handleListChange('steps', index, e.target.value)}
                                    placeholder="Describe this cooking step..."
                                    rows={1}
                                    style={{ 
                                        height: 'auto', 
                                        overflow: 'hidden', 
                                        resize: 'none' 
                                    }}
                                    onInput={(e) => {
                                        e.target.style.height = 'auto';
                                        e.target.style.height = e.target.scrollHeight + 'px';
                                    }}
                                />
                                {formData.steps.length > 1 && (
                                    <button
                                        type="button"
                                        className="remove-item-btn"
                                        onClick={() => removeListItem('steps', index)}
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        ))}
                        <button
                            type="button"
                            className="add-item-btn small-text"
                            onClick={() => addListItem('steps')}
                        >
                            + Add Step
                        </button>
                    </div>
                </div>

                {/* AI Context Generation Section */}
                <div className="form-section ai-enhancement-section">
                    <h3 className="section-title">AI Recipe Enhancement</h3>
                    <p style={{ color: 'var(--color-medium-gray)', marginBottom: 'var(--spacing-md)', fontSize: '0.9rem' }}>
                        Enhance your recipe with AI by providing a URL to an existing recipe. The AI will combine your inputs with the reference recipe to create a more detailed version.
                    </p>
                    
                    <div className="form-group">
                        <label className="form-label">Reference Recipe URL</label>
                        <input
                            type="url"
                            value={urlContext}
                            onChange={(e) => setUrlContext(e.target.value)}
                            className="form-input"
                            placeholder="https://example.com/recipe..."
                            disabled={isGenerating}
                        />
                    </div>

                    {aiGenerated && (
                        <div className="ai-success-badge">
                            ✅ Recipe enhanced with AI!
                            {Object.keys(highlightFields).some(key => highlightFields[key]) && (
                                <div style={{ fontSize: '0.75rem', marginTop: '0.25rem', opacity: 0.9 }}>
                                    Enhanced: {Object.keys(highlightFields).filter(key => highlightFields[key]).join(', ')}
                                </div>
                            )}
                        </div>
                    )}

                    {isGenerating && (
                        <div className="ai-loading-message" style={{ marginTop: 'var(--spacing-md)' }}>
                            🤖 AI is analyzing the reference recipe and enhancing your inputs. This may take a few moments...
                        </div>
                    )}
                </div>

                {error && <div className="error-message compact">{error}</div>}
                
                <div className="button-group">
                    <button
                        type="button"
                        onClick={handleAIGeneration}
                        disabled={isGenerating || !urlContext.trim()}
                        className={`ai-button ${isGenerating || !urlContext.trim() ? 'disabled' : ''}`}
                    >
                        {isGenerating ? '🔄 Generating...' : '✨ Enhance with AI'}
                    </button>

                    <button type="submit" className="create-submit-button">
                        Create Recipe
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateRecipe;